import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';
import clsx from 'clsx';
import { DISPATCH_EVENTS, KEYBOARD_EVENTS } from '../../../utils/constants/commonConstants';
/**
 * @Description Custom Tooltip component to display information on hover
 * @param content Tooltip message content
 * @param direction Tooltip position with respect to target element
 * @param children Elements for which the Tooltip is to be shown on hover
 * @param usePortal Whether to render tooltip in a React Portal (renders to document.body)
 *
 * ## When to use `usePortal={true}`:
 *
 * Use Portal rendering when the tooltip needs to escape CSS constraints:
 *
 * 1. **Parent has `overflow: hidden/auto/scroll`**
 *    - Tooltip gets clipped by scrollable containers
 *    - Example: Tables, modals, sidebars with scrolling
 *
 * 2. **Complex stacking contexts**
 *    - Tooltip appears behind other elements despite high z-index
 *    - Parent elements have `position: sticky`, `position: fixed`, or `z-index` values
 *    - Example: Sticky table headers, fixed navigation bars
 *
 * 3. **Nested positioned elements**
 *    - Tooltip is inside multiple layers of positioned elements
 *    - z-index doesn't work across stacking contexts
 *
 * ## When NOT to use `usePortal`:
 *
 * - Simple tooltips in normal document flow (default behavior is sufficient)
 * - No overflow or z-index issues present
 * - Performance-critical scenarios (portal adds minimal overhead but avoid if unnecessary)
 *
 * @example
 * // Regular tooltip (no portal needed)
 * <Tooltip content="Simple tooltip" direction="top">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Portal tooltip (escapes table overflow)
 * <Tooltip content="Tooltip in table" direction="top" usePortal={true}>
 *   <TableCell>Content</TableCell>
 * </Tooltip>
 */
interface TooltipProps {
    content: string | React.ReactNode;
    direction: 'top' | 'right' | 'bottom' | 'left';
    isBenchmarking?: boolean;
    alwaysVisible?: boolean;
    inverted?: boolean;
    small?: boolean;
    containerFullWidth?: boolean;
    fitContent?: boolean;
    containerFullHeight?: boolean;
    children?: React.ReactNode;
    height?: string;
    style?: React.CSSProperties;
    isDealNotesTooltip?: boolean;
    isOverviewDetail?: boolean;
    hideDelay?: number;
    /**
     * Delay in ms before the tooltip opens on hover, so brushing past a trigger on the way
     * somewhere else doesn't flash it. The pending open is cancelled if the pointer leaves first,
     * or if another portal tooltip opens. Click/tap and keyboard activation ignore it — those are
     * deliberate, so there is no hover intent to wait for.
     * @default 0
     */
    showDelay?: number;
    flexible?: boolean;
    contentCss?: React.CSSProperties;
    /**
     * Render tooltip in a React Portal to escape overflow clipping and stacking context issues.
     * Set to `true` when tooltip appears behind other elements or gets clipped by parent containers.
     * @default false
     */
    usePortal?: boolean;
    /**
     * Called every time the tooltip transitions to visible (hover/focus). Useful for lazy-fetching
     * content on demand. Callers are responsible for their own dedup if needed.
     */
    onShow?: () => void;
    /**
     * Suppress the built-in CSS arrow (`::before`). Use when the design has no arrow indicator.
     * @default false
     */
    hideArrow?: boolean;
    /**
     * Do not dispatch `TOOLTIP_PORTAL_SHOW` when this tooltip opens. Use when two related portal
     * tooltips need to coexist (e.g. a name tooltip nested inside an overview tooltip on the same
     * trigger) — without this, each would close the other on hover.
     * @default false
     */
    disablePortalShowDispatch?: boolean;
    /**
     * Portal-only: anchor the tip to the pointer and track the cursor instead of the trigger's box.
     * @default false
     */
    followCursor?: boolean;
    /**
     * Open on click/tap instead of hover. Needed on touch devices where hover doesn't fire.
     * Toggles on trigger click and closes on any click outside the trigger or the tip.
     * @default false
     */
    triggerOnClick?: boolean;
    /**
     * Extra class added to the outer `.Tooltip-Tip` element. Use for static styling overrides
     * (size, background, radius) instead of inline `style` — keeps presentation in `.module.scss`.
     */
    tipClassName?: string;
    /**
     * Extra class added to the inner `.Content` element. Use for static styling overrides
     * (padding, background, border-radius, box-shadow) instead of inline `contentCss`.
     */
    contentClassName?: string;
}

/** Only the fields the portal positioning reads — a DOMRect satisfies it, as does a pointer. */
type Rect = { top: number; bottom: number; left: number; right: number; width: number };

const Tooltip = ({
    content = '',
    direction = 'top',
    children,
    alwaysVisible,
    inverted,
    small,
    containerFullWidth,
    fitContent,
    containerFullHeight,
    isBenchmarking,
    height = '',
    style,
    isDealNotesTooltip,
    isOverviewDetail,
    hideDelay = 0,
    showDelay = 0,
    flexible = false,
    contentCss,
    usePortal = false,
    followCursor = false,
    onShow,
    hideArrow = false,
    disablePortalShowDispatch = false,
    triggerOnClick = false,
    tipClassName,
    contentClassName,
}: TooltipProps) => {
    const [active, setActive] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [clampTick, setClampTick] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const moveFrameRef = useRef<number | null>(null);

    useEffect(() => {
        if (alwaysVisible) setActive(true);
    }, [alwaysVisible]);

    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            if (moveFrameRef.current !== null) {
                cancelAnimationFrame(moveFrameRef.current);
            }
        };
    }, []);

    /* 
        Listen for any other portal tooltip opening, and force-close ourselves.
        Decoupled cross-instance coordination via DOM event — no shared module state.
    */
    useEffect(() => {
        if (!usePortal) return;
        const handler = (e: Event) => {
            if ((e as CustomEvent).detail === wrapperRef.current) return; // it's us opening
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            clearPendingShow();
            setActive(false);
        };
        document.addEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
        return () => document.removeEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
    }, [usePortal]);

    // Keep portal tooltip inside the viewport on all 4 edges.
    useLayoutEffect(() => {
        if (!active || !usePortal || !tooltipRef.current) return;
        const r = tooltipRef.current.getBoundingClientRect();
        const PAD = 8;
        const dx = Math.max(PAD - r.left, Math.min(0, window.innerWidth - PAD - r.right));
        const dy = Math.max(PAD - r.top, Math.min(0, window.innerHeight - PAD - r.bottom));
        if (dx || dy) setTooltipPosition((p) => ({ top: p.top + dy, left: p.left + dx }));
    }, [active, usePortal, clampTick]);

    /*
        followCursor anchors on a zero-width box at the pointer, which collapses every direction to
        the same point — so open and move MUST share this mapping or the tip jumps on first move.
        position: fixed → coordinates stay in viewport space (no scroll offsets).
    */
    const anchorRect = (e?: React.MouseEvent): Rect =>
        followCursor && e
            ? { top: e.clientY, bottom: e.clientY, left: e.clientX, right: e.clientX, width: 0 }
            : (wrapperRef.current as HTMLDivElement).getBoundingClientRect();

    const positionFrom = (rect: Rect) =>
        direction === 'right'
            ? { top: rect.top, left: rect.right } // Right edge of trigger
            : direction === 'left'
            ? { top: rect.top, left: rect.left } // Left edge of trigger
            : direction === 'bottom'
            ? { top: rect.bottom, left: rect.left + rect.width / 2 } // Below, centered
            : { top: rect.top, left: rect.left + rect.width / 2 }; // Above, centered

    const clearPendingShow = () => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
    };

    /*
        The open itself, split out of showTip so a delayed open can run it later.
        Takes the anchor rect rather than the event: under showDelay the React event is pooled long
        before this runs. Everything here is deferred with the open — dispatching TOOLTIP_PORTAL_SHOW
        early would close a sibling portal tooltip for a hover that never opens, and onShow is
        documented as firing when the tooltip becomes visible, which lazy-fetching callers rely on.
    */
    const revealTip = (rect: Rect) => {
        // Portal rendering: Calculate absolute position relative to viewport
        // This is needed because portal renders to document.body, outside the normal DOM hierarchy
        if (usePortal && wrapperRef.current) {
            if (!disablePortalShowDispatch) {
                document.dispatchEvent(new CustomEvent(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, { detail: wrapperRef.current }));
            }

            setTooltipPosition(positionFrom(rect));
            setClampTick((t) => t + 1); // Force viewport clamp to re-run for the new position
        }

        onShow?.();
        setActive(true);
    };

    const showTip = (e?: React.MouseEvent) => {
        if (alwaysVisible) return;
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        const rect = anchorRect(e); // read now — the React event is pooled

        if (showDelay > 0 && !active) {
            clearPendingShow();
            showTimeoutRef.current = setTimeout(() => {
                showTimeoutRef.current = null;
                revealTip(rect);
            }, showDelay);
            return;
        }

        revealTip(rect);
    };

    /*
        Reposition only — showTip would re-dispatch TOOLTIP_PORTAL_SHOW and re-fire onShow per pixel.
        Coalesced into one frame: a high-poll mouse fires mousemove faster than the display paints,
        and each update makes the clamp effect read the tip's rect (a forced layout).
    */
    const moveTip = (e: React.MouseEvent) => {
        if (alwaysVisible || !active) return;
        const next = positionFrom(anchorRect(e)); // read now — the React event is pooled
        if (moveFrameRef.current !== null) return;
        moveFrameRef.current = requestAnimationFrame(() => {
            moveFrameRef.current = null;
            setTooltipPosition(next);
            setClampTick((t) => t + 1);
        });
    };

    const hideTip = () => {
        if (alwaysVisible) return;
        clearPendingShow();
        if (hideDelay > 0) {
            hideTimeoutRef.current = setTimeout(() => {
                setActive(false);
            }, hideDelay);
        } else {
            setActive(false);
        }
    };

    // Click-to-toggle (touch devices). Opens immediately: showDelay exists to detect hover intent,
    // and a tap has already declared it.
    const toggleTip = () => {
        clearPendingShow();
        if (active) setActive(false);
        else revealTip(anchorRect());
    };

    // Keyboard support for click-triggered tooltips: Enter/Space toggles like a click,
    // Escape dismisses. Matches the role="button" + tabIndex + onKeyDown pattern used
    // for other click-toggle triggers in this codebase (e.g. LookalikeTable's EntityPill).
    const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
            e.preventDefault();
            toggleTip();
        } else if (e.key === KEYBOARD_EVENTS.ESCAPE && active) {
            setActive(false);
        }
    };

    const onOutside = useCallback((e: MouseEvent) => {
        const target = e.target as Node;
        if (wrapperRef.current?.contains(target)) return;
        if (tooltipRef.current?.contains(target)) return;
        setActive(false);
    }, []);

    // While a click-triggered tooltip is open, close it on any click outside the
    // trigger or the tip itself. The opening click already finished before this
    // listener attaches, so it won't self-close.
    useEffect(() => {
        if (!triggerOnClick || !active) return;
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [triggerOnClick, active, onOutside]);

    const renderTooltipContent = () => {
        const tooltipStyles: React.CSSProperties = {
            color: inverted ? 'white' : '#979797',
            wordBreak: 'break-word',
            whiteSpace: inverted ? 'nowrap' : 'pre-wrap',
            width: fitContent ? 'fit-content' : '',
            minWidth: small ? '80px' : '200px',
            height: height || 'fit-content',
            overflowY: height ? 'auto' : 'hidden',
            overflowX: 'hidden',
            display: content ? 'inline-block' : 'none',
            zIndex: 1000,
            scrollbarWidth: isOverviewDetail ? 'none' : 'auto',
            ...style,
        };

        // Portal-specific positioning: Use fixed positioning to escape parent constraints
        // This allows the tooltip to appear above overflow:hidden containers and complex stacking contexts
        if (usePortal) {
            tooltipStyles.position = 'fixed'; // Fixed relative to viewport, not parent
            tooltipStyles.top = `${tooltipPosition.top}px`; // Absolute position from viewport top
            tooltipStyles.left = `${tooltipPosition.left}px`; // Absolute position from viewport left
            tooltipStyles.zIndex = 999999; // Very high z-index to appear above all elements
            // A tip sitting under the cursor would steal the hover from its own trigger and flicker.
            if (followCursor) tooltipStyles.pointerEvents = 'none';
            tooltipStyles.transform =
                direction === 'right'
                    ? 'translate(0.5rem, 0)' // Sit to the right of the trigger with an 8px gap
                    : direction === 'left'
                    ? 'translate(calc(-100% - 0.5rem), 0)' // Sit to the left of the trigger with an 8px gap
                    : direction === 'bottom'
                    ? 'translate(-50%, 0.625rem)' // Center and position below
                    : 'translate(-50%, calc(-100% - 0.625rem))'; // Center and position above (default/top)
        }

        return (
            <div
                ref={tooltipRef}
                className={clsx(
                    'Tooltip-Tip',
                    // Portal mode positions via inline fixed coords; direction classes carry
                    // `!important` rules that would override that, so skip them when portaling.
                    !usePortal && direction,
                    {
                        DealNotesTooltipBottom: !usePortal && isDealNotesTooltip && direction === 'bottom',
                        BenchmarkTooltip: isBenchmarking,
                        NoArrow: hideArrow,
                    },
                    tipClassName,
                )}
                style={tooltipStyles}
                // In portal mode this div is detached from the wrapper's DOM subtree (rendered via
                // createPortal to document.body), so moving the mouse off the trigger and onto the
                // tip itself would otherwise fire the wrapper's onMouseLeave and close it mid-crossing
                // — before the mouse ever reaches interactive content (e.g. a button) inside the tip.
                // No-op for non-portal tooltips, where the tip is already a DOM child of the wrapper.
                onMouseEnter={usePortal && !triggerOnClick ? showTip : undefined}
                onMouseLeave={usePortal && !triggerOnClick ? hideTip : undefined}
            >
                <div className={clsx('Content', { OverviewDetail: isOverviewDetail }, contentClassName)} style={contentCss}>
                    {content}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={wrapperRef}
            className="Tooltip-Wrapper"
            onMouseEnter={triggerOnClick ? undefined : showTip}
            onMouseMove={followCursor && usePortal && !triggerOnClick ? moveTip : undefined}
            onMouseLeave={triggerOnClick ? undefined : hideTip}
            onClick={triggerOnClick ? toggleTip : undefined}
            onKeyDown={triggerOnClick ? onTriggerKeyDown : undefined}
            role={triggerOnClick ? 'button' : undefined}
            tabIndex={triggerOnClick ? 0 : undefined}
            aria-expanded={triggerOnClick ? active : undefined}
            style={{
                width: containerFullWidth ? '100%' : '',
                height: containerFullHeight ? '100%' : '',
            }}
        >
            {children}
            {/* Normal rendering: Tooltip is a child of the wrapper (respects parent CSS) */}
            {active && !usePortal && renderTooltipContent()}
            {/* Portal rendering: Tooltip renders to document.body (escapes parent CSS constraints) */}
            {active && usePortal && createPortal(renderTooltipContent(), document.body)}
        </div>
    );
};

export default Tooltip;
